import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { unopenableAppointmentKinds } from 'src/constants/appointmentKind';
import { nonFormNoteSchemas } from 'src/constants/note-schemas';
import { useFlagEnabled } from 'src/flagging/hooks';
import { useAppointmentsAndNotes, useMemberAppointmentsAndNotes } from './useAppointmentsAndNotes';
import { useMember } from './useMember';
import { getMemberAppointmentIndex, } from './useMyAppointments';
import { usePastInteractions } from './usePastInteractions';
import { getRiskFromLastSignedNote } from './useRiskFromLastSignedNote';
import { useSignedNotes } from './useSignedNotes';
import { useTodoNotes } from './useTodoNotes';
import { useUpcomingAppointments } from './useUpcomingAppointments';
/**
 * @deprecated use usePagedSignedNotes.  Remove this with cleanup of ENABLE_SIGNED_PAGING flag
 */
export var useFlaggedSignedAppointmentsAndNotes = function () {
    var _a = useSignedNotes(), notesWithPlaceholders = _a.notesWithPlaceholders, isLoading = _a.isLoading, isError = _a.isError;
    return {
        apptData: notesWithPlaceholders,
        isLoading: isLoading,
        isError: isError,
    };
};
export var useFlaggedToDoAppointmentsAndNotes = function () {
    var fetchToDoFromOnlyEHR = useFlagEnabled('FETCH_TODO_NOTES_FROM_ONLY_EHR');
    if (!fetchToDoFromOnlyEHR) {
        var _a = useAppointmentsAndNotes(), notesWithPlaceholders = _a.notesWithPlaceholders, isLoading = _a.isLoading, isError = _a.isError;
        return {
            notesAndPlaceholders: notesWithPlaceholders === null || notesWithPlaceholders === void 0 ? void 0 : notesWithPlaceholders.filter(function (nop) { return (!nop.note || !nop.note.signedAt) && DateTime.fromISO(nop.date) < DateTime.now(); }),
            isError: isError,
            isLoading: isLoading,
        };
    }
    else {
        var _b = useTodoNotes(), notesWithPlaceholders = _b.notesWithPlaceholders, isLoading = _b.isLoading, isError = _b.isError;
        return {
            notesAndPlaceholders: notesWithPlaceholders,
            isError: isError,
            isLoading: isLoading,
        };
    }
};
export var useFlaggedUpcomingAppointmentsAndNotes = function () {
    var fetchUpcomingFromOnlyEHR = useFlagEnabled('FETCH_UPCOMING_NOTES_FROM_ONLY_EHR');
    if (!fetchUpcomingFromOnlyEHR) {
        var _a = useAppointmentsAndNotes(), appointments_1 = _a.appointments, notesIndexedByAppointmentId = _a.notesIndexedByAppointmentId, isLoading = _a.isLoading, isError = _a.isError;
        var appointmentIndex = useMemo(function () {
            return getMemberAppointmentIndex(appointments_1 || []);
        }, [appointments_1]);
        return {
            apptData: appointments_1 === null || appointments_1 === void 0 ? void 0 : appointments_1.filter(function (appt) { return DateTime.fromISO(appt.start_at) > DateTime.now(); }),
            appointments: appointments_1,
            notesIndexedByAppointmentId: notesIndexedByAppointmentId,
            appointmentIndex: appointmentIndex,
            isLoading: isLoading,
            isError: isError,
        };
    }
    else {
        var _b = useUpcomingAppointments(), appointments = _b.appointments, isLoading = _b.isLoading, isError = _b.isError;
        return {
            apptData: appointments,
            isLoading: isLoading,
            isError: isError,
        };
    }
};
export var useFlaggedPastInteractions = function (memberId) {
    var fetchingPastInteractionsFromOnlyEHR = useFlagEnabled('FETCH_PAST_INTERACTIONS_FROM_ONLY_EHR');
    var kareoFlagEnabled = useFlagEnabled('KAREO_NOTES');
    var mmCoachingFlagEnabled = useFlagEnabled('ENABLE_MM_COACHING_APPTS');
    if (!fetchingPastInteractionsFromOnlyEHR) {
        var memberFetch = useMember(memberId);
        var _a = useMemberAppointmentsAndNotes(memberFetch === null || memberFetch === void 0 ? void 0 : memberFetch.data), notesWithPlaceholders = _a.notesWithPlaceholders, isLoading = _a.isLoading, isError = _a.isError, providersById = _a.providersById;
        var pastNotesWithPlaceholders = notesWithPlaceholders === null || notesWithPlaceholders === void 0 ? void 0 : notesWithPlaceholders.filter(function (nwp) {
            var _a;
            return DateTime.fromISO(nwp.date) < DateTime.now() &&
                (kareoFlagEnabled || ((_a = nwp.note) === null || _a === void 0 ? void 0 : _a.noteSchema) !== nonFormNoteSchemas.legacyImport) &&
                (mmCoachingFlagEnabled ||
                    !nwp.appointment ||
                    !unopenableAppointmentKinds.includes(nwp.appointment.kind));
        });
        return {
            notesWithPlaceholders: pastNotesWithPlaceholders,
            isLoading: isLoading,
            isError: isError,
            isProviderQueryResultsLoading: providersById.isLoading,
        };
    }
    else {
        var _b = usePastInteractions(memberId), notesWithPlaceholders = _b.notesWithPlaceholders, isLoading = _b.isLoading, isError = _b.isError;
        return {
            notesWithPlaceholders: notesWithPlaceholders,
            isLoading: isLoading,
            isError: isError,
            isProviderQueryResultsLoading: false,
        };
    }
};
export var useFlaggedRiskConcerns = function (appointmentId, appointments, notesIndexedByAppointmentId, start_at, memberId, riskConcerns, last_appointment) {
    var _a, _b;
    var fetchUpcomingFromOnlyEHR = useFlagEnabled('FETCH_UPCOMING_NOTES_FROM_ONLY_EHR');
    if (!fetchUpcomingFromOnlyEHR) {
        var note = notesIndexedByAppointmentId && notesIndexedByAppointmentId[appointmentId];
        var lastSigned = getRiskFromLastSignedNote(appointments, start_at, memberId, note, notesIndexedByAppointmentId);
        var riskForHarmToOthers = lastSigned.riskForHarmToOthers, riskForHarmToSelf = lastSigned.riskForHarmToSelf, date = lastSigned.date;
        return {
            rfhToOthers: riskForHarmToOthers,
            rfhToSelf: riskForHarmToSelf,
            date: date,
            signedAt: note === null || note === void 0 ? void 0 : note.signedAt,
        };
    }
    else {
        return {
            rfhToOthers: riskConcerns === null || riskConcerns === void 0 ? void 0 : riskConcerns.riskForHarmToOthers,
            rfhToSelf: riskConcerns === null || riskConcerns === void 0 ? void 0 : riskConcerns.riskForHarmToSelf,
            date: (_a = last_appointment === null || last_appointment === void 0 ? void 0 : last_appointment.note) === null || _a === void 0 ? void 0 : _a.signedAt,
            signedAt: (_b = last_appointment === null || last_appointment === void 0 ? void 0 : last_appointment.note) === null || _b === void 0 ? void 0 : _b.signedAt,
        };
    }
};
